import { toArray } from 'lodash';
import { toJS } from 'mobx';

/**
 * ActivityInstanceUserNavigationService
 *
 * A utility service for navigating between users (i.e. students for most cases) in activity instances.
 * Currently used by DocReaderModal and FileViewerModal for Previous/Next user navigation,
 * and can be potentially used by other similar components (such as LearnosityScoringModal)
 * in the future.
 *
 * This service provides helper methods for:
 * - Finding next/previous users with circular wraparound
 * - Determining if navigation should be enabled
 * - Formatting user names consistently
 * - Getting current index and total count
 *
 * The service handles both Arrays and MobX Observable Maps seamlessly.
 */

export default class ActivityInstanceUserNavigationService {

  /**
   * Get the next activity instance for a user, with circular wraparound
   * @param {Array|Map} instances - Collection of activity instances (Map keys are instance IDs)
   * @param {string} currentInstanceId - Current activity instance ID
   * @returns {Object|null} Next activity instance for a user, or null if not found
   */
  static getNextActivityInstanceForUser(instances, currentInstanceId) {
    if (!instances) {
      return null;
    }
    instances = toJS(instances);

    // Convert to array efficiently
    const instancesArray = Array.isArray(instances)
      ? instances
      : Array.from(instances.values());

    const currentIndex = instancesArray.findIndex((instance) => instance.id === currentInstanceId);

    if (currentIndex === -1) {
      return null;
    }

    const nextIndex = currentIndex === instancesArray.length - 1 ? 0 : currentIndex + 1;
    return instancesArray[nextIndex];
  }

  /**
   * Get the previous activity instance for a user, with circular wraparound
   * @param {Array|Map} instances - Collection of activity instances (Map keys are instance IDs)
   * @param {string} currentInstanceId - Current activity instance ID
   * @returns {Object|null} Previous activity instance for a user, or null if not found
   */
  static getPreviousActivityInstanceForUser(instances, currentInstanceId) {
    if (!instances) {
      return null;
    }
    instances = toJS(instances);

    // Convert to array efficiently
    const instancesArray = Array.isArray(instances)
      ? instances
      : Array.from(instances.values());

    const currentIndex = instancesArray.findIndex((instance) => instance.id === currentInstanceId);

    if (currentIndex === -1) {
      return null;
    }

    const prevIndex = currentIndex === 0 ? instancesArray.length - 1 : currentIndex - 1;
    return instancesArray[prevIndex];
  }

  /**
   * Get the current activity instance for a user by ID
   * @param {Array|Map} instances - Collection of activity instances (Map keys are instance IDs)
   * @param {string} currentInstanceId - Current activity instance ID
   * @returns {Object|null} Current activity instance for a user, or null if not found
   */
  static getCurrentActivityInstanceForUser(instances, currentInstanceId) {
    if (!instances || !currentInstanceId) {
      return null;
    }
    instances = toJS(instances);

    // If Array, find by id
    if (Array.isArray(instances)) {
      return instances.find((instance) => instance.id === currentInstanceId) || null;
    }

    // Otherwise assume Map and use .get() directly since the key is the instance ID
    return instances?.get?.(currentInstanceId) || null;
  }

  /**
   * Determine if navigation should be enabled based on instance count
   * @param {Array|Map} instances - Collection of activity instances
   * @param {Object} currentActivityInstance - Current activity instance (optional for validation)
   * @returns {boolean} True if there is more than one user to navigate between
   */
  static shouldEnableNavigation(instances, currentActivityInstance = null) {
    if (!instances) {
      return false;
    }
    instances = toJS(instances);
    const instancesArray = toArray(instances);

    // Navigation requires at least 2 instances
    if (instancesArray.length < 2) {
      return false;
    }

    // Optional: Validate that current instance exists in the instances
    if (currentActivityInstance && currentActivityInstance.id) {
      const exists = instancesArray.some((instance) => instance.id === currentActivityInstance.id);
      return exists;
    }

    return true;
  }

  /**
   * Format a user name from an activity instance
   * @param {Object} instance - Activity instance with firstName and lastName
   * @returns {string} Formatted name "FirstName LastName"
   */
  static formatActivityInstanceUserName(instance) {
    if (!instance) {
      return '';
    }
    const firstName = instance.firstName || '';
    const lastName = instance.lastName || '';
    return `${firstName} ${lastName}`.trim();
  }

  /**
   * Get the current user index (zero-based) from the instances
   * @param {Array|Map} instances - Collection of activity instances
   * @param {string} currentInstanceId - Current activity instance ID
   * @returns {number} Zero-based index, or -1 if not found
   */
  static getCurrentUserIndex(instances, currentInstanceId) {
    if (!instances) {
      return -1;
    }
    instances = toJS(instances);
    const instancesArray = toArray(instances);
    return instancesArray.findIndex(instance => instance.id === currentInstanceId);
  }

  /**
   * Get the total number of users from the instances
   * @param {Array|Map} instances - Collection of activity instances
   * @returns {number} Total count of instances
   */
  static getTotalUsers(instances) {
    if (!instances) {
      return 0;
    }
    instances = toJS(instances);
    const instancesArray = toArray(instances);
    return instancesArray.length;
  }
}
